import type { Session } from './types';
import { publishCatalogChangeForRequest } from './catalog-change-sync';

let csrfToken = '';

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    public readonly details?: unknown
  ) {
    super(message);
  }
}

export function setCsrfToken(token: string): void {
  csrfToken = token;
}

async function parseError(response: Response): Promise<ApiError> {
  try {
    const body = await response.json() as { error?: { code?: string; message?: string; details?: unknown } };
    return new ApiError(
      body.error?.code ?? 'REQUEST_FAILED',
      body.error?.message ?? '请求失败，请稍后重试',
      response.status,
      body.error?.details
    );
  } catch {
    return new ApiError('REQUEST_FAILED', '请求失败，请稍后重试', response.status);
  }
}

export async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  const isForm = options.body instanceof FormData;
  if (options.body && !isForm && !headers.has('content-type')) headers.set('content-type', 'application/json');
  if (options.method && !['GET', 'HEAD'].includes(options.method.toUpperCase()) && csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }
  const response = await fetch(path, { ...options, headers, credentials: 'same-origin' });
  if (!response.ok) {
    const error = await parseError(response);
    if (typeof window !== 'undefined' && error.code === 'PERMISSION_REQUIRED') window.dispatchEvent(new Event('account-permissions-changed'));
    throw error;
  }
  const result = response.status === 204 ? undefined as T : await response.json() as T;
  publishCatalogChangeForRequest(path, options.method);
  return result;
}

export async function download(path: string, options: RequestInit = {}): Promise<Blob> {
  const headers = new Headers(options.headers);
  if (options.method && !['GET', 'HEAD'].includes(options.method.toUpperCase()) && csrfToken) {
    headers.set('x-csrf-token', csrfToken);
  }
  const response = await fetch(path, { ...options, headers, credentials: 'same-origin' });
  if (!response.ok) {
    const error = await parseError(response);
    if (typeof window !== 'undefined' && error.code === 'PERMISSION_REQUIRED') window.dispatchEvent(new Event('account-permissions-changed'));
    throw error;
  }
  const result = await response.blob();
  publishCatalogChangeForRequest(path, options.method);
  return result;
}

export async function getSession(): Promise<Session | null> {
  try {
    const session = await request<Session>('/api/v1/auth/me');
    setCsrfToken(session.csrfToken);
    return session;
  } catch (error) {
    if (error instanceof ApiError && error.status === 401) return null;
    throw error;
  }
}

export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1_000);
}
